/**
 * Permission checking utility
 * Checks if user has access to specific modules/actions
 */

/**
 * Check if user has any permission for a module
 * @param {string[]} userPermissions - Array of user's permissions
 * @param {string} module - Module name (e.g., 'documents', 'boxes', 'users')
 * @returns {boolean}
 */
export function hasModuleAccess(userPermissions, module) {
  if (!userPermissions || !Array.isArray(userPermissions)) return false
  return userPermissions.some(p => p.includes(`admin.${module}.`))
}

/**
 * Check if user has a specific permission
 * @param {string[]} userPermissions - Array of user's permissions
 * @param {string} permission - Full permission string (e.g., 'admin.documents.index')
 * @returns {boolean}
 */
export function hasPermission(userPermissions, permission) {
  if (!userPermissions || !Array.isArray(userPermissions)) return false
  return userPermissions.includes(permission)
}

/**
 * Check if user has any of the specified permissions
 * @param {string[]} userPermissions - Array of user's permissions
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean}
 */
export function hasAnyPermission(userPermissions, permissions) {
  if (!userPermissions || !Array.isArray(userPermissions)) return false
  return permissions.some(p => userPermissions.includes(p))
}

/**
 * Check if user has access to settings (any settings-related module)
 * @param {string[]} userPermissions - Array of user's permissions
 * @returns {boolean}
 */
export function hasSettingsAccess(userPermissions) {
  if (!userPermissions || !Array.isArray(userPermissions)) return false
  
  const settingsModules = [
    'box-types',
    'diagnoses',
    'treatments',
    'roles',
    'categories',
    'document-types',
    'cities',
    'settings',
  ]
  
  return settingsModules.some(module => hasModuleAccess(userPermissions, module))
}

/**
 * Module to permissions mapping for sidebar items
 */
export const modulePermissions = {
  documents: ['admin.documents.index'],
  boxes: ['admin.boxes.index'],
  users: ['admin.users.index'],
  settings: [
    'admin.settings.index',
    'admin.box-types.index',
    'admin.diagnoses.index',
    'admin.treatments.index',
    'admin.roles.index',
    'admin.categories.index',
    'admin.document-types.index',
    'admin.cities.index',
  ],
}

/**
 * Settings tab to permissions mapping
 */
export const settingsTabPermissions = {
  'box-types': ['admin.box-types.index'],
  'diagnoses': ['admin.diagnoses.index'],
  'treatments': ['admin.treatments.index'],
  'roles': ['admin.roles.index'],
  'backup': ['admin.settings.index'], // Backup requires settings permission
}
